<template>
  <div class="bouncesTab">
    <!-- Toolbar -->
    <div class="tabToolbar">
      <input
        class="input filterSearch"
        type="text"
        v-model="search"
        placeholder="Search email"
        @input="onSearchInput"
      />
    </div>

    <div v-if="loading && rows.length === 0" class="emptyMsg">Loading…</div>
    <div v-else-if="rows.length === 0 && !loading" class="emptyMsg">No bounced emails found.</div>
    <div v-else class="tableWrap">
      <table class="table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Source</th>
            <th>Bounced at</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.id">
            <td class="mono">{{ row.email }}</td>
            <td>{{ row.source }}</td>
            <td class="mono">{{ formatDateTime(row.bounced_at) }}</td>
            <td>
              <button
                class="btnRemove"
                title="Remove bounce record (allows sending to this email again)"
                @click="removeBounce(row.id)"
              >✕</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="rows.length > 0" class="tableFooter">
      <span class="hint">Showing {{ rows.length }} of {{ total }} bounce{{ total === 1 ? '' : 's' }}</span>
      <button v-if="hasMore" class="btn" :disabled="loading" @click="loadMore">
        {{ loading ? 'Loading…' : 'Load more' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { apiFetch } from '../../services/api.ts'

const PAGE_SIZE = 100

const rows = ref([])
const loading = ref(false)
const total = ref(0)
const currentOffset = ref(0)
const search = ref('')
let _searchDebounce = null

const hasMore = computed(() => rows.value.length < total.value)

function formatDateTime(dt) {
  if (!dt) return '—'
  const d = new Date(dt)
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  )
}

function buildUrl(offset = 0) {
  const params = new URLSearchParams()
  params.set('limit', String(PAGE_SIZE))
  params.set('offset', String(offset))
  const trimmed = search.value.trim()
  if (trimmed) params.set('q', trimmed)
  return `/email-bounces?${params.toString()}`
}

async function reload() {
  loading.value = true
  try {
    const resp = await apiFetch(buildUrl(0))
    rows.value = Array.isArray(resp?.bounces) ? resp.bounces : []
    total.value = typeof resp?.total === 'number' ? resp.total : rows.value.length
    currentOffset.value = rows.value.length
  } catch {
    // non-fatal
  } finally {
    loading.value = false
  }
}

async function loadMore() {
  if (!hasMore.value || loading.value) return
  loading.value = true
  try {
    const resp = await apiFetch(buildUrl(currentOffset.value))
    const newRows = Array.isArray(resp?.bounces) ? resp.bounces : []
    rows.value = [...rows.value, ...newRows]
    total.value = typeof resp?.total === 'number' ? resp.total : rows.value.length
    currentOffset.value = rows.value.length
  } catch {
    // non-fatal
  } finally {
    loading.value = false
  }
}

async function removeBounce(bounceId) {
  try {
    await apiFetch(`/email-bounces/${bounceId}`, { method: 'DELETE' })
    rows.value = rows.value.filter((r) => r.id !== bounceId)
    total.value = Math.max(0, total.value - 1)
  } catch {
    // non-fatal
  }
}

function onSearchInput() {
  if (_searchDebounce) clearTimeout(_searchDebounce)
  _searchDebounce = setTimeout(() => {
    _searchDebounce = null
    reload()
  }, 350)
}

onMounted(() => reload())
</script>

<style scoped>
.bouncesTab {
  display: flex;
  flex-direction: column;
}

.tabToolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
  flex-wrap: wrap;
}

.filterSearch {
  flex: 1;
  min-width: 200px;
  max-width: 280px;
}

.input {
  height: 36px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--text);
  font-size: 13px;
  padding: 0 10px;
}

.input:focus {
  outline: none;
  border-color: var(--accent);
}

.tableWrap {
  overflow-x: auto;
  width: 100%;
}

.tableFooter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
}

.hint {
  color: var(--muted);
  font-size: 12px;
}

.emptyMsg {
  color: var(--muted);
  font-size: 13px;
  padding: 20px 0;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
}

.btnRemove {
  background: none;
  border: none;
  color: var(--muted);
  cursor: pointer;
  font-size: 14px;
  padding: 2px 6px;
  border-radius: 4px;
}

.btnRemove:hover {
  color: #ef4444;
  background: var(--surface);
}
</style>
